import { AccountManagerDTO, AccountManagerDTO_EmptyObject } from 'src/app/core/models/system/account-manager/account-manager.dto';
import { AccountManagersService } from 'src/app/core/services/utelize/system/account-manager/account-managers-service';
import { AfterViewInit, Component, EventEmitter, OnDestroy, OnInit, Output, TemplateRef, ViewChild } from '@angular/core';
import { AlertClassColour, ConfigSystemParams, msSize } from 'src/app/core/consts';
import { AuthenticationService } from 'src/app/core/services/auth.service';
import { catchError, firstValueFrom, throwError } from 'rxjs';
import { CompletedModalFormComponent } from 'src/app/forms-modal/_pop-ups/completed-modal-form/completed-modal-form.component';
import { ConfigSystemService } from 'src/app/core/services/utelize/system/config-system/config-system-service';
import { ExceptionsService } from 'src/app/core/services/utelize/general/exceptions-service';
import { FormControl, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { LastPassKillerService } from 'src/app/core/services/utelize/general/last-pass-killer-service';
import { ModalForceCloseService } from 'src/app/core/services/utelize/general/modal-force-close-service';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { PageCompHeaderComponent } from 'src/app/core/page-components/page-comp-header/page-comp-header.component';
import { SystemUserDTO } from 'src/app/core/models/system/user/system-user.dto';

@Component({
  selector: 'app-account-manager-modal-form',
  templateUrl: './account-manager-modal-form.component.html',
  styleUrls: ['./account-manager-modal-form.component.scss']
})

export class AccountManagerModalFormComponent implements AfterViewInit, OnDestroy, OnInit {

  @Output() accountManagerDataHasBeenSaved: EventEmitter<void>=new EventEmitter<void>();
  @Output() accountManagerFormCancelled   : EventEmitter<void>=new EventEmitter<void>();

  @ViewChild('accountManagerModalFormContent', { static: false }) accountManagerModalFormContent!: TemplateRef<void>;
  @ViewChild('completedModalForm',             { static: false }) completedModalForm!            : CompletedModalFormComponent;
  @ViewChild(PageCompHeaderComponent                            ) pageHeader!                    : PageCompHeaderComponent;

  get modalBackGround() { let value: string | boolean=this.configSystemService.getValue(ConfigSystemParams.ModalBackGround, "TRUE");
                          return value=value.toUpperCase()=="STATIC" ? "static" : (value.toUpperCase()=="TRUE" ? true : false)     ; }

  private accountManager             : AccountManagerDTO | undefined;
  private accountManagerModalFormRef!: NgbModalRef;
  private accountManagerSavedMessage : string="Changes applied.";
  private curUser                    : SystemUserDTO;
  private pendingPhoto               : File | undefined;
  private removeExistingPhoto        : boolean=false;

  public accountManagerForm!   : UntypedFormGroup;
  public accountManagerId      : number=0;
  public alertColourProgress   : string=AlertClassColour.Grey;
  public contactNumberMaxLength: number=30;
  public emailMaxLength        : number=200;
  public formSubmitted         : boolean=false;
  public formTitle             : string="Account Manager";
  public nameMaxLength         : number=100;
  public photoPreviewUrl       : string="";
  public saving                : boolean=false;

  constructor(
    private accountManagersService: AccountManagersService,
    private authenticationService : AuthenticationService,
    private configSystemService   : ConfigSystemService,
    private exceptionsService     : ExceptionsService,
    private formBuilder           : UntypedFormBuilder,
    private lastPassKillerService : LastPassKillerService,
    private modalForceCloseService: ModalForceCloseService,
    private modalService          : NgbModal
  ) {
    this.curUser=this.authenticationService.currentUserObject();
  }

  public ngOnInit(): void {
    try {
      this.setUpForm();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public ngAfterViewInit(): void {
    try {
      this.lastPassKillerService.start();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public ngOnDestroy(): void {
    try {
      this.revokePreview();
      this.lastPassKillerService.stop();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public async openForm(accountManagerId: number): Promise<void> {
    try {
      this.accountManagerId   =accountManagerId;
      this.formTitle          =accountManagerId>0 ? "Edit Account Manager" : "Create Account Manager";
      this.formSubmitted      =false;
      this.saving             =false;
      this.alertColourProgress=AlertClassColour.Grey;
      this.pendingPhoto       =undefined;
      this.removeExistingPhoto=false;
      this.revokePreview();
      this.setUpForm();

      await this.getAccountManagerAndOpenModal(accountManagerId);
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  private async getAccountManagerAndOpenModal(accountManagerId: number): Promise<void> {
    try {
      if (accountManagerId>0) {
        const data=await firstValueFrom(this.accountManagersService.getById(accountManagerId)
                                        .pipe(
                                          catchError(error => {
                                            throw new Error(this.exceptionsService.formatHttpError(error));
                                          })
                                        ));

        this.accountManager  =data;
        this.accountManagerId=data.id;
        this.populateFormFields();
        this.loadPhotoPreview();
        this.openModal();
        return;
      }

      this.accountManager=AccountManagerDTO_EmptyObject();
      this.populateFormFields();
      this.openModal();
    }
    catch(e) {
      this.accountManagerFormCancelled.emit();
      this.exceptionsService.handleException(<Error>e);
    }
  }

  private openModal(): void {
    try {
      this.accountManagerModalFormRef=this.modalService.open(this.accountManagerModalFormContent, {
        backdrop     : this.modalBackGround,
        beforeDismiss: () => { return this.modalForceCloseService.isForcing(); },
        centered     : true,
        keyboard     : false,
        size         : msSize.Medium
      });
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  //#region Form

  get formData() {
    return this.accountManagerForm.controls;
  }

  private setUpForm(): void {
    try {
      this.removeFormFields(this.accountManagerForm);
      this.accountManagerForm=this.formBuilder.group({ });
      this.addFormFields(this.accountManagerForm);
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  private addFormControl(form: UntypedFormGroup, dataField: string, value: string, validators: Validators): void {
    try {
      form.addControl(dataField, new FormControl(value, validators));
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  private addFormFields(formGroup: UntypedFormGroup): void {
    try {
      this.addFormControl(formGroup, "active",        "", []);
      this.addFormControl(formGroup, "contactNumber", "", [Validators.maxLength(this.contactNumberMaxLength)]);
      this.addFormControl(formGroup, "email",         "", [Validators.required, Validators.email, Validators.maxLength(this.emailMaxLength)]);
      this.addFormControl(formGroup, "id",            "", []);
      this.addFormControl(formGroup, "name",          "", [Validators.required, Validators.maxLength(this.nameMaxLength)]);
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  private removeFormFields(form: UntypedFormGroup): void {
    try {
      if (form != undefined) {
        for (let control in form.controls) {
          form.removeControl(control);
        }
      }
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  private populateFormFields(): void {
    try {
      if (this.accountManager != undefined) {
        this.accountManagerForm.controls["active"       ].setValue(this.accountManager.active       );
        this.accountManagerForm.controls["contactNumber"].setValue(this.accountManager.contactNumber);
        this.accountManagerForm.controls["email"        ].setValue(this.accountManager.email        );
        this.accountManagerForm.controls["id"           ].setValue(this.accountManager.id           );
        this.accountManagerForm.controls["name"         ].setValue(this.accountManager.name         );
      }
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  //#endregion

  //#region Photo

  get hasPhotoPreview(): boolean {
    return this.photoPreviewUrl != "" && !this.removeExistingPhoto;
  }

  private loadPhotoPreview(): void {
    try {
      if (this.accountManager?.hasPhoto && this.accountManager.photoUrl) {
        this.accountManagersService.getPhotoObjectUrl(this.accountManager.photoUrl).subscribe({
          next : url   => {
            this.photoPreviewUrl=url;
          },
          error: error => {
            this.exceptionsService.handleException(new Error(error));
          }
        });
      }
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public onPhotoSelected(event: Event): void {
    try {
      const input=<HTMLInputElement>event.target;
      const file =input.files?.[0];

      if (file==undefined)
        return;

      const allowed =["image/png", "image/jpeg", "image/webp"];
      const maxBytes=2 * 1024 * 1024;

      if (!allowed.includes(file.type)) {
        this.exceptionsService.handleException(new Error("AccountManagerModalFormComponent.onPhotoSelected() - Unsupported image type. Allowed: PNG, JPEG, WebP."));
        return;
      }

      if (file.size>maxBytes) {
        this.exceptionsService.handleException(new Error("AccountManagerModalFormComponent.onPhotoSelected() - Image exceeds the 2 MB limit."));
        return;
      }

      this.revokePreview();
      this.pendingPhoto       =file;
      this.removeExistingPhoto=false;
      this.photoPreviewUrl    =URL.createObjectURL(file);
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public removePhoto(): void {
    try {
      this.revokePreview();
      this.pendingPhoto       =undefined;
      this.photoPreviewUrl    ="";
      this.removeExistingPhoto=true;
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  private revokePreview(): void {
    try {
      if (this.photoPreviewUrl.startsWith("blob:"))
        URL.revokeObjectURL(this.photoPreviewUrl);

      this.photoPreviewUrl="";
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  //#endregion

  //#region Submit/Cancel

  get canSubmit(): boolean {
    return this.accountManagerForm.valid && !this.saving;
  }

  public accountManagerFormSubmitAttempt(): void {
    try {
      if (this.saving)
        return;

      this.formSubmitted=true;

      if (this.accountManagerForm.valid) {
        this.saving=true;
        this.prepareToSaveAccountManager();
      }
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  private prepareToSaveAccountManager(): void {
    try {
      if (this.accountManager==undefined) {
        this.saving=false;
        return;
      }

      this.accountManager.active       =this.accountManagerForm.controls["active"].value ?? true;
      this.accountManager.contactNumber=this.accountManagerForm.controls["contactNumber"].value ?? "";
      this.accountManager.email        =this.accountManagerForm.controls["email"].value;
      this.accountManager.enteredBy    =this.curUser.userId;
      this.accountManager.id           =this.accountManagerForm.controls["id"].value ?? 0;
      this.accountManager.name         =this.accountManagerForm.controls["name"].value;

      const save$=this.accountManagerId>0
                  ? this.accountManagersService.put(this.accountManager)
                  : this.accountManagersService.post(this.accountManager);

      save$.pipe(
        catchError((error) => {
          return throwError(() => new Error(this.exceptionsService.formatHttpError(error)));
        })
      )
      .subscribe({
        next : data  => {
          this.processPhotoThenComplete(data);
        },
        error: error => {
          this.alertColourProgress=AlertClassColour.Red;
          this.exceptionsService.handleException(new Error(error));
        }
      });
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  private processPhotoThenComplete(saved: AccountManagerDTO): void {
    try {
      const savedId=saved?.id ?? this.accountManagerId;

      if (this.pendingPhoto != undefined && savedId>0) {
        this.accountManagersService.uploadPhoto(savedId, this.pendingPhoto)
        .pipe(
          catchError((error) => {
            return throwError(() => new Error(this.exceptionsService.formatHttpError(error)));
          })
        )
        .subscribe({
          next : () => { this.saveSucceeded(); },
          error: error => {
            this.saving=false;
            this.alertColourProgress=AlertClassColour.Red;
            this.exceptionsService.handleException(new Error(error));
          }
        });
        return;
      }

      if (this.removeExistingPhoto && savedId>0 && this.accountManager?.hasPhoto) {
        this.accountManagersService.deletePhoto(savedId)
        .pipe(
          catchError((error) => {
            return throwError(() => new Error(this.exceptionsService.formatHttpError(error)));
          })
        )
        .subscribe({
          next : () => {
            this.saveSucceeded();
          },
          error: error => {
            this.saving=false;
            this.alertColourProgress=AlertClassColour.Red;
            this.exceptionsService.handleException(new Error(error));
          }
        });
        return;
      }

      this.saveSucceeded();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  private saveSucceeded(): void {
    try {
      this.saving             =false;
      this.alertColourProgress=AlertClassColour.Green;
      this.accountManagerDataHasBeenSaved.emit();
      this.completedModalForm.openForm(this.accountManagerSavedMessage, "");
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public accountManagerFormCancel(): void {
    try {
      this.accountManagerFormCancelled.emit();
      this.accountManagerModalFormRef.close();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public accountManagerCompletedModalHasBeenClosed(): void {
    try {
      this.accountManagerModalFormRef.close();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  //#endregion
}
